import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';

import { setNotificationRead } from '@actions/notifications-actions';

import NotificationContents from '@components/header/notification-contents';

import { ButtonBase, Tooltip } from '@mui';

import { renderItemAvatar } from '@utils/logs-utils';

import './notification-item.scss';

const NotificationLinkWrap = ({ link, setRead, children }) => {
  const dispatch = useDispatch();
  if (link) {
    return (
      <ButtonBase
        onClick={() => {
          if (setRead) {
            setRead();
          }
          let linkValue = link;
          if (typeof link === 'object') {
            linkValue = link.pathname;
          }
          dispatch(push({ pathname: linkValue, state: { source: location.pathname } }));
        }}
        styleName="notification-item-link"
      >
        { children }
      </ButtonBase>
    );
  }
  return (
    <div styleName="notification-item-link">
      { children }
    </div>
  );
};

NotificationLinkWrap.propTypes = {
  children: PropTypes.node,
  link: PropTypes.object,
  setRead: PropTypes.func
};

const NotificationItem = ({ notification }) => {
  const dispatch = useDispatch();
  const agencies = useSelector(state => state.dataTypes.agency);

  const markAsRead = useCallback(notif => {
    dispatch(setNotificationRead(notif.id, !notif.read));
  }, [dispatch]);

  const { entry_object, read } = notification;
  const { attrs, entry } = entry_object;
  const { action, created } = entry;

  const setRead = useCallback(() => {
    dispatch(setNotificationRead(notification.id, true));
  }, [dispatch, notification.id]);

  // Inject the agency name into the user object:
  let { user } = entry;
  if (user && user.agency) {
    user = {
      ...user,
      agency_name: agencies[user.agency].name
    };
  }

  return (
    <div styleName="notification-item-container">
      <NotificationLinkWrap link={attrs.link} setRead={setRead}>
        <div styleName="item-avatar">
          {renderItemAvatar(user, 'medium')}
        </div>
        <div styleName="item-content">
          <div styleName="item-main item-row">
            <div styleName="item-message">
              <NotificationContents action={action} attrs={attrs} user={user} created={created} />
            </div>
          </div>
        </div>
      </NotificationLinkWrap>
      <div styleName="item-read">
        {/* Was nested inside the NotificationLinkWrap button above - axe's nested-interactive
            rule flags any role="button" (native or ARIA) with focusable descendants, since
            browsers/AT don't reliably expose them. Moved out as a sibling control instead. */}
        <div
          onClick={() => markAsRead(notification)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              markAsRead(notification);
            }
          }}
          role="button"
          tabIndex="0"
          styleName="not-read-icon-wrapper"
        >
          {read && (
            <Tooltip title="mark as unread" placement="top-start">
              <div styleName="read-icon mark-as-unread" />
            </Tooltip>
          )}
          {!read && (
            <Tooltip title="mark as read" placement="top-start">
              <div styleName="read-icon mark-as-read" />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.object
};

export default NotificationItem;
